import React, { useState, useEffect, useCallback } from 'react';
import { Button, Typography, Chip } from "@material-tailwind/react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import DriverLicenseVerification from '../DriverLicenseVerification';
import { rootUrl } from '../../../../config/config';
import axios from 'axios';

const DriverLicenseVerificationsList = ({ driverId, driver, user }) => {
    const [verifications, setVerifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState('ALL');
    const token = localStorage.getItem('authToken');

    // Helper for API calls
    const apiCall = useCallback(async (method, endpoint, data = null) => {
        const config = {
            method,
            url: `${rootUrl}${endpoint}`,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        };
        if (data) config.data = data;
        return axios(config);
    }, [token]);

    const fetchVerifications = useCallback(async () => {
        if (!driverId) return;
        setLoading(true);
        try {
            const response = await apiCall('GET', `/driver-license/${driverId}/list?limit=100`);
            if (response.data && response.data.success) {
                setVerifications(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching verifications:", error);
        } finally {
            setLoading(false);
        }
    }, [driverId, apiCall]);

    useEffect(() => {
        fetchVerifications();
    }, [fetchVerifications]);

    const handleVerificationSuccess = () => {
        setShowModal(false);
        fetchVerifications();
    };

    const filteredVerifications = verifications.filter(v => {
        if (filterStatus === 'ALL') return true;
        return v.verification_status === filterStatus;
    });

    const getStatusColor = (status) => {
        switch (status) {
            case 'VERIFIED': return 'green';
            case 'FAILED': return 'red';
            case 'PENDING': return 'amber';
            default: return 'blue-gray';
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return 'N/A';
        return new Date(isoString).toLocaleDateString() + ' ' + new Date(isoString).toLocaleTimeString();
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2">
                    <Typography variant="h6" color="blue-gray">
                        License Verifications
                    </Typography>
                    <Chip value={filteredVerifications.length} size="sm" variant="ghost" />
                </div>
                <div className="flex gap-2">
                    <select 
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="ALL">All Status</option>
                        <option value="VERIFIED">Verified</option>
                        <option value="FAILED">Failed</option>
                        <option value="PENDING">Pending</option>
                    </select>
                    <Button 
                        size="sm" 
                        className="flex items-center gap-2 bg-[#FF5733]"
                        onClick={() => setShowModal(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        New Verification
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-8 text-gray-500">Loading verifications...</div>
            ) : filteredVerifications.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-100">
                    <Typography color="gray">No verification records found.</Typography>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">License No</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sticker</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified By</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredVerifications.map((v) => (
                                <tr key={v.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDate(v.created_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <Chip 
                                            value={v.verification_status || 'UNKNOWN'} 
                                            color={getStatusColor(v.verification_status)}
                                            size="sm"
                                            variant="ghost"
                                            className="w-fit"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {v.license_no || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {v.license_expiry ? new Date(v.license_expiry).toLocaleDateString() : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {v.sticker_link ? (
                                            <a 
                                                href={v.sticker_link} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline flex items-center gap-1"
                                            >
                                                <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                                                View
                                            </a>
                                        ) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {v.created_by || 'System'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal for adding verification */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl relative">
                        <button 
                            onClick={() => setShowModal(false)} 
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl z-10"
                        >
                            &times;
                        </button>
                        <div className="p-1">
                            <DriverLicenseVerification 
                                apiCall={apiCall}
                                user={user}
                                initial={{ 
                                    driver_id: driverId,
                                    driver_name: driver?.name,
                                    license_no: (!driver?.license_no || driver?.license_no === 'Unknown') ? '' : driver.license_no,
                                    license_expiry: (!driver?.license_expiry || driver?.license_expiry === 'no licence exp') ? '' : driver.license_expiry
                                }}
                                onSuccess={handleVerificationSuccess}
                                onClose={() => setShowModal(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DriverLicenseVerificationsList;